// src/odoo/customers/components/CustomerProductInsights.tsx
// All comments are in English.

import React from "react";
import type { DateRangeQuery } from "../api/overviewApi";
import {
  useCustomerProductSummary,
  useCustomerProductPurchases,
} from "../hooks/useProductInsights";
import { AlertCircle } from "lucide-react";
import {
  CustomerProductSummaryCard,
  CustomerProductSummaryExplanation,
} from "./CustomerProductSummaryCard";
import {
  CustomerProductsPurchasedTable,
  CustomerProductsPurchasedExplanation,
} from "./CustomerProductsPurchasedTable";

interface CustomerProductInsightsProps {
  companyPartnerId?: number;
  dateRange: DateRangeQuery;
}

export const CustomerProductInsights: React.FC<CustomerProductInsightsProps> = ({
  companyPartnerId,
  dateRange,
}) => {
  const {
    data: summary,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useCustomerProductSummary(companyPartnerId, dateRange);

  const {
    data: purchases,
    isLoading: isPurchasesLoading,
    error: purchasesError,
  } = useCustomerProductPurchases(companyPartnerId, dateRange);

  const isLoading = isSummaryLoading || isPurchasesLoading;
  const error = summaryError || purchasesError;

  const cleanedProducts =
    purchases?.products.filter((p) => !!p.product_name) ?? [];

  const totalGmvInRange: number =
    purchases?.total_gmv != null ? purchases.total_gmv : 0;

  if (!companyPartnerId) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-sm text-muted-foreground">
        Please select a company to see product insights.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[3fr_1fr]">
        <div className="space-y-4">
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-3 h-4 w-24 animate-pulse rounded bg-muted" />
            <div className="grid grid-cols-2 gap-3">
              <div className="h-16 animate-pulse rounded bg-muted" />
              <div className="h-16 animate-pulse rounded bg-muted" />
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-3 h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-40 animate-pulse rounded bg-muted" />
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-40 rounded-lg border bg-card p-4 shadow-sm" />
          <div className="h-40 rounded-lg border bg-card p-4 shadow-sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">Failed to load product insights.</p>
          <p className="mt-1 text-xs opacity-80">
            Please try again later or adjust the filters.
          </p>
        </div>
      </div>
    );
  }

  if (!summary && cleanedProducts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-sm text-muted-foreground">
        No product data is available for this company in the selected period.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Row 1: Purchase summary + explanation */}
      <div className="grid gap-4 lg:grid-cols-[3fr_1fr]">
        <CustomerProductSummaryCard
          summary={summary as any}
          totalGmvInRange={totalGmvInRange}
          productCountInRange={cleanedProducts.length}
          dateRange={dateRange}
        />

        {/* Explanation component defined in CustomerProductSummaryCard.tsx */}
        <CustomerProductSummaryExplanation />
      </div>

      {/* Row 2: Products purchased + explanation */}
      <div className="grid gap-4 lg:grid-cols-[3fr_1fr]">
        <CustomerProductsPurchasedTable products={cleanedProducts as any} />

        {/* Explanation component moved into CustomerProductsPurchasedTable.tsx */}
        <CustomerProductsPurchasedExplanation />
      </div>
    </div>
  );
};
